import {
  type ComponentProps,
  type FC,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useState,
} from 'react';
import { Ripple } from './Ripple';
import { cn } from './react';
import { Spinner } from './Spinner';

export type ButtonProps = {
  size: 12 | 10 | 6 | 5 | 8;
  fab?: boolean;
  variant:
    | 'primary'
    | 'brand'
    | 'negative'
    | 'positive'
    | 'ghost'
    | 'transparent'
    | 'outline'
    | 'link'
    | 'text';
  block?: boolean;
  loading?: boolean;
  children?: ReactNode;
  disabled?: boolean;
  className?: string;
  autoFocus?: boolean;
  type?: ComponentProps<'button'>['type'] | 'link' | 'div';
  onClick?: (
    e: ReactMouseEvent<HTMLElement, MouseEvent>,
  ) => Promise<unknown> | unknown;
  onContextMenu?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  'aria-label'?: string;
};

export const Button: FC<ButtonProps> = ({
  size,
  fab,
  variant,
  block,
  className,
  loading,
  children,
  disabled,
  type,
  onClick,
  ...rest
}) => {
  const [localLoading, setLocalLoading] = useState(false);
  const hasPadding = variant !== 'link' && variant !== 'text';
  const RootComponent =
    type === 'link' ? 'a' : type === 'div' ? 'div' : 'button';

  return (
    <RootComponent
      className={cn(
        block ? 'flex' : 'inline-flex',
        'items-center justify-center gap-1',
        'border relative overflow-hidden',
        'cursor-pointer',
        'disabled:pointer-events-none',
        'transition-all',
        hasPadding
          ? 'hover:brightness-95 active:shadow-inner/5 active:brightness-95 disabled:bg-background-disabled disabled:text-content-tertiary'
          : 'hover:brightness-75 active:brightness-90 disabled:text-content-tertiary',
        variant === 'brand' &&
          'bg-content-brand border-transparent text-shades-black-90',
        variant === 'negative' &&
          'bg-content-negative border-transparent text-content-primary-inverse',
        variant === 'positive' &&
          'bg-content-positive border-transparent text-content-primary-inverse',
        variant === 'primary' &&
          'bg-background-inverse border-border-disabled/10 text-content-primary-inverse',
        variant === 'ghost' && 'bg-background-disabled border-transparent',
        variant === 'transparent' && 'bg-background-primary border-transparent',
        variant === 'link' &&
          'bg-transparent border-transparent text-content-link disabled:line-through',
        variant === 'text' &&
          'bg-transparent border-transparent text-content-tertiary',
        variant === 'outline' && 'bg-background-primary border-border-disabled',
        size === 12 && [
          'h-12 text-base',
          hasPadding && 'rounded-xl',
          fab ? 'w-12' : hasPadding && 'px-4',
        ],
        size === 10 && [
          'h-10 text-sm',
          hasPadding && 'rounded-lg',
          fab ? 'w-10' : hasPadding && 'px-3',
        ],
        size === 8 && [
          'h-8 text-xs',
          hasPadding && 'rounded-md',
          fab ? 'w-8' : hasPadding && 'px-3',
        ],
        size === 6 && [
          'h-6 text-xs',
          hasPadding && 'rounded-sm',
          fab ? 'w-6' : hasPadding && 'px-2',
        ],
        size === 5 && [
          'h-5 text-xs',
          hasPadding && 'rounded-sm',
          fab ? 'w-5' : hasPadding && 'px-1',
        ],
        className,
      )}
      {...(RootComponent === 'button' &&
        ({
          type: type ?? 'button',
        } as Record<never, never>))}
      disabled={disabled || loading || localLoading}
      onClick={(e) => {
        const result = onClick?.(e);
        if (result instanceof Promise) {
          setLocalLoading(true);
          result.finally(() => setLocalLoading(false));
        }
      }}
      {...rest}
    >
      {loading || localLoading ? <Spinner className="h-1/2" /> : children}
      <Ripple />
    </RootComponent>
  );
};
